import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Package, MapPin, AlertCircle, Store, Truck, Loader2, Coins } from "lucide-react";
import { fetchProducts, fetchHomeAddresses, shipProducts, previewShipping, type Product, type HomeAddress, type ShippingPreview } from "@/services/customerService";
import { toast } from "sonner";
import ResponsiveImage from "@/components/ResponsiveImage";

const OripaProductsConfirmPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lotteryResultIds = location.state?.lotteryResultIds || [];

  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [address, setAddress] = useState<HomeAddress | null>(null);
  const [shippingPreview, setShippingPreview] = useState<ShippingPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [shipping, setShipping] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setPreviewError(null);
      const previewPromise = lotteryResultIds.length > 0
        ? previewShipping(lotteryResultIds).catch((err: any) => {
            const message = err?.response?.data?.error || '配送料の取得に失敗しました';
            setPreviewError(message);
            return null;
          })
        : Promise.resolve(null);
      const [products, addresses, preview] = await Promise.all([
        fetchProducts(),
        fetchHomeAddresses(),
        previewPromise,
      ]);
      const selected = products.filter(p => lotteryResultIds.includes(p.lottery_result_id));
      setSelectedProducts(selected);
      const defaultAddress = addresses.find(a => a.default) || addresses[0] || null;
      setAddress(defaultAddress);
      setShippingPreview(preview);
    } catch (error) {
      toast.error('データの取得に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const productsBySite = useMemo(() => {
    const grouped = new Map<number, { site: Product['site']; products: Product[] }>();
    selectedProducts.forEach(product => {
      if (!grouped.has(product.site.id)) {
        grouped.set(product.site.id, { site: product.site, products: [] });
      }
      grouped.get(product.site.id)!.products.push(product);
    });
    return Array.from(grouped.values());
  }, [selectedProducts]);

  const handleConfirmShipping = async () => {
    try {
      setShipping(true);
      const result = await shipProducts(lotteryResultIds);
      toast.success(result.message);
      if (result.errors && result.errors.length > 0) {
        result.errors.forEach(err => toast.warning(err));
      }
      navigate('/products');
    } catch (error: any) {
      toast.error(error.response?.data?.error || '発送依頼に失敗しました');
      console.error(error);
    } finally {
      setShipping(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>配送内容の確認</title>
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div className="pt-20 pb-12 max-w-screen-lg mx-auto px-4">
          <div className="mb-6">
            <Link to="/products" className="inline-flex items-center gap-2 px-3 py-2 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] text-sm hover:bg-gray-100 transition-colors mb-4">
              <ArrowLeft className="h-4 w-4" />
              賞品一覧に戻る
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-400 border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                <Truck className="h-5 w-5 text-black" />
              </div>
              <h1 className="text-2xl [font-weight:var(--theme-font-weight)] text-black">配送内容の確認</h1>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                <Package className="h-4 w-4 text-black" />
              </div>
              <h2 className="text-lg [font-weight:var(--theme-font-weight)] text-black">配送する賞品</h2>
            </div>
            {loading ? (
              <div className="text-center py-6">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-500" />
                <p className="mt-2 [font-weight:var(--theme-font-weight-button)] text-gray-500">読み込み中...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {productsBySite.map(({ site, products }) => (
                  <div key={site.id} className="bg-white border-theme rounded-theme-lg shadow-theme p-4">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]">
                      <div className="w-6 h-6 bg-blue-400 border-theme rounded flex items-center justify-center">
                        <Store className="h-3 w-3 text-black" />
                      </div>
                      <h3 className="[font-weight:var(--theme-font-weight)] text-sm text-black">{site.name}</h3>
                      <span className="px-2 py-0.5 bg-gray-100 border-theme rounded text-xs [font-weight:var(--theme-font-weight-button)]">{products.length}件</span>
                    </div>
                    <div className="space-y-3">
                      {products.map(product => {
                        const isInvalid = shippingPreview?.invalid_lottery_result_ids.includes(product.lottery_result_id) ?? false;
                        return (
                          <div key={product.lottery_result_id} className={`flex gap-3 p-2 border-theme rounded-theme ${isInvalid ? 'bg-gray-200 opacity-60' : 'bg-gray-50'}`}>
                            <div className="w-16 bg-white border-theme rounded-theme overflow-hidden flex-shrink-0" style={{ aspectRatio: '3/4' }}>
                              <ResponsiveImage src={product.image_urls[0]} variants={product.image_variant_urls} alt={product.name} displaySize="card" className="w-full h-full object-contain" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-sm [font-weight:var(--theme-font-weight)] text-black mb-1">{product.name}</h4>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-block px-2 py-0.5 bg-theme-primary border-theme rounded text-xs [font-weight:var(--theme-font-weight-button)]">{product.rarity}</span>
                                {isInvalid && (
                                  <span className="inline-block px-2 py-0.5 bg-red-200 border-2 border-red-500 text-red-900 rounded text-xs [font-weight:var(--theme-font-weight)]">発送対象外</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {productsBySite.length > 1 && (
              <div className="mt-4 bg-blue-100 border-2 border-blue-400 rounded-theme p-3">
                <div className="flex gap-2">
                  <AlertCircle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-blue-900">複数の店舗からの商品が含まれているため、店舗ごとに個別に配送されます。</p>
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-green-400 border-theme rounded-theme flex items-center justify-center">
                <MapPin className="h-4 w-4 text-black" />
              </div>
              <h2 className="text-lg [font-weight:var(--theme-font-weight)] text-black">配送先住所</h2>
            </div>

            {loading ? (
              <div className="text-center py-6">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-500" />
                <p className="mt-2 [font-weight:var(--theme-font-weight-button)] text-gray-500">読み込み中...</p>
              </div>
            ) : address ? (
              <div className="bg-white border-theme rounded-theme-lg shadow-theme p-4">
                <div className="space-y-2 p-3 bg-gray-100 border-theme rounded-theme">
                  <p className="text-sm"><span className="[font-weight:var(--theme-font-weight-button)] text-gray-600">氏名:</span> <span className="[font-weight:var(--theme-font-weight)] text-black">{address.name}</span></p>
                  <p className="text-sm"><span className="[font-weight:var(--theme-font-weight-button)] text-gray-600">郵便番号:</span> <span className="[font-weight:var(--theme-font-weight)] text-black">〒{address.postal_code}</span></p>
                  <p className="text-sm"><span className="[font-weight:var(--theme-font-weight-button)] text-gray-600">住所:</span> <span className="[font-weight:var(--theme-font-weight)] text-black">{address.address_line1}{address.address_line2 && <><br />{address.address_line2}</>}</span></p>
                  <p className="text-sm"><span className="[font-weight:var(--theme-font-weight-button)] text-gray-600">電話番号:</span> <span className="[font-weight:var(--theme-font-weight)] text-black">{address.phone_number}</span></p>
                </div>
                <div className="mt-4">
                  <Link to="/home-address" state={{ from: '/products/confirm', lotteryResultIds }} className="inline-flex items-center gap-2 px-4 py-2 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] text-sm hover:bg-gray-100 shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all">住所を変更</Link>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-100 border-4 border-yellow-500 rounded-theme-lg p-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-4 w-4 text-black" />
                  </div>
                  <div className="flex-1">
                    <p className="[font-weight:var(--theme-font-weight)] text-black mb-2">配送先住所が登録されていません</p>
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-700 mb-3">商品を発送するには、配送先住所を登録してください。</p>
                    <Link to="/home-address" state={{ from: '/products/confirm', lotteryResultIds }} className="inline-flex items-center gap-2 px-4 py-2 bg-theme-primary border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] text-sm shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all">住所を登録する</Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {previewError && (
            <div className="mb-6 bg-red-100 border-2 border-red-500 rounded-theme p-4">
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-red-900">{previewError}</p>
              </div>
            </div>
          )}

          {shippingPreview && shippingPreview.invalid_lottery_result_ids.length > 0 && (
            <div className="mb-6 bg-yellow-100 border-2 border-yellow-500 rounded-theme p-4">
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-700 flex-shrink-0 mt-0.5" />
                <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-yellow-900">
                  「発送対象外」バッジの商品は発送依頼できません（既に発送済みまたはポイント変換済み）。これらの商品を除いた残りの商品のみを発送します。
                </p>
              </div>
            </div>
          )}

          {shippingPreview && shippingPreview.total_shipping_fee > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-yellow-400 border-theme rounded-theme flex items-center justify-center">
                  <Coins className="h-4 w-4 text-black" />
                </div>
                <h2 className="text-lg [font-weight:var(--theme-font-weight)] text-black">配送料</h2>
              </div>
              <div className="bg-white border-theme rounded-theme-lg shadow-theme p-4 space-y-3">
                {shippingPreview.site_breakdown.length > 1 && (
                  <div className="space-y-2 pb-3 border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]">
                    {shippingPreview.site_breakdown.map((breakdown, idx) => (
                      <div key={`${breakdown.site_id}-${idx}`} className="flex items-center justify-between text-sm">
                        <span className="[font-weight:var(--theme-font-weight-button)] text-gray-700 truncate pr-2">
                          {breakdown.site_name}
                          {breakdown.event_name && (
                            <span className="text-gray-500 ml-1">/ {breakdown.event_name}</span>
                          )}
                        </span>
                        <span className="[font-weight:var(--theme-font-weight)] text-black flex-shrink-0">
                          {breakdown.prepaid ? '支払済み' : `${breakdown.shipping_fee.toLocaleString()}pt`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="[font-weight:var(--theme-font-weight)] text-black">合計配送料</span>
                  <span className="text-xl [font-weight:var(--theme-font-weight)] text-black">
                    {shippingPreview.total_shipping_fee.toLocaleString()}pt
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span className="[font-weight:var(--theme-font-weight-button)]">現在のポイント残高</span>
                  <span className="[font-weight:var(--theme-font-weight)]">
                    {shippingPreview.customer_balance.toLocaleString()}pt
                  </span>
                </div>
                {!shippingPreview.sufficient_balance && (
                  <div className="bg-red-100 border-2 border-red-500 rounded-theme p-3">
                    <div className="flex gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-red-900">
                        ポイントが不足しています。発送依頼には{shippingPreview.total_shipping_fee.toLocaleString()}pt必要です。
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleConfirmShipping}
              disabled={
                !address ||
                loading ||
                shipping ||
                previewError != null ||
                (shippingPreview != null && !shippingPreview.sufficient_balance)
              }
              className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-blue-500 border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-white shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all disabled:bg-gray-300 disabled:shadow-none disabled:translate-x-0 disabled:translate-y-0 disabled:cursor-not-allowed"
            >
              {shipping ? (<><Loader2 className="h-5 w-5 animate-spin" />発送依頼中...</>) : (<><Truck className="h-5 w-5" />この内容で発送する</>)}
            </button>
            <p className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500 text-center">発送後のキャンセルはできません。内容をよくご確認ください。</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default OripaProductsConfirmPage;
